import type { FC } from 'react';
import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { chakra, HStack, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import useCountriesList from 'common/countries/useCountriesList';
import { SEPA_COUNTRIES_CODES } from 'common/sepa';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import * as Layout from 'layouts/StickyStepperLayout';

import locales from './locales';
import {
  RecipientInformationFormSchema,
  recipientInformationFormSchema,
} from './schema';

interface RecipientInformationFormProps {
  onSubmit: SubmitHandler<RecipientInformationFormSchema>;
  defaultValues?: DefaultValues<RecipientInformationFormSchema>;
}

const RecipientInformationForm: FC<RecipientInformationFormProps> = ({
  defaultValues,
  onSubmit,
}) => {
  const form = useForm<RecipientInformationFormSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(recipientInformationFormSchema),
  });

  const sepaCountries = useCountriesList()
    .filter((country) => SEPA_COUNTRIES_CODES.includes(country.value))
    .map(({ emoji, label, value }) => ({
      key: value,
      label: `${emoji} ${label}`,
      value,
    }));

  const { isValid } = form.formState;

  return (
    <chakra.form display="contents" onSubmit={form.handleSubmit(onSubmit)}>
      <Layout.ContentsContainer gap="space-40">
        <VStack align="flex-start" spacing="space-16">
          <Heading size="lg">{locales.title}</Heading>
          <Text size="lg">{locales.description}</Text>
        </VStack>
        <VStack alignItems="stretch" spacing="space-24">
          <Form.Field
            error={form.formState.errors.name?.message}
            label={locales.labels.name}
          >
            <Form.Input
              placeholder={locales.placeholders.name}
              {...form.register('name')}
            />
          </Form.Field>

          <VStack align="stretch">
            <Form.Field
              error={form.formState.errors.country?.message}
              label={locales.labels.country}
            >
              <Controller
                control={form.control}
                name="country"
                render={({ field }) => (
                  <Form.SunshineSelect
                    components={{
                      Option: Form.SunshineSelect.components.LanguageOption,
                    }}
                    isSearchable
                    onChange={(option) => {
                      if (option) {
                        form.setValue('country', option.value);
                        form.trigger(['iban', 'swiftBic']);
                      }
                    }}
                    options={sepaCountries}
                    value={sepaCountries.find(
                      (country) => country.value === field.value,
                    )}
                  />
                )}
              />
            </Form.Field>
          </VStack>

          <HStack alignItems="flex-start" spacing="space-16">
            <Form.IbanInputField
              control={form.control}
              flex={8}
              name="iban"
              placeholder="FR12 0000 0000 0000 0000 0000 000"
            />

            <Form.Field
              error={form.formState.errors.swiftBic?.message}
              flex={4}
              label={locales.labels.swiftBic}
            >
              <Form.Input
                placeholder="FRCLX XXXX"
                {...form.register('swiftBic')}
              />
            </Form.Field>
          </HStack>
        </VStack>
      </Layout.ContentsContainer>
      <Layout.FooterContainer>
        <StepperProgressBar />

        <Layout.FooterButtonsContainer>
          <Button isDisabled={!isValid} type="submit">
            {locales.submit}
          </Button>
        </Layout.FooterButtonsContainer>
      </Layout.FooterContainer>
    </chakra.form>
  );
};

export default RecipientInformationForm;
